import { Component, OnInit } from '@angular/core';
import { CourseRepositoryService } from '../../../courses/services/course-repository.service';
import { StudentCourseEntity } from '../../../../meta-data/app-meta-data.service';
import { CourseProgressItem } from '../../../courses/models/course-progress-hierarchy.model';
import { IntegrationRepositoryService } from '../../../integration/services/integration-repository.service';
import { SchoolRepositoryService } from '../../../schools/services/school-repository.service';
import { UnlimitedCourseAccessService } from '../../../courses/services/unlimited-course-access.service';
import { map, mergeMap } from 'rxjs/operators';

@Component({
  selector: 'app-main-dashboard',
  templateUrl: './main-dashboard.component.html',
  styleUrls: ['./main-dashboard.component.scss']
})
export class MainDashboardComponent implements OnInit {
  studentCourses: StudentCourseEntity[];
  currentStudentCourse: StudentCourseEntity;
  currentCourseProgress: CourseProgressItem;

  readonly DashboardSections = DashboardSections;
  readonly courseSkeletonCards = [1, 2];
  readonly assignmentSkeletonCards = [1, 2];
  readonly assignmentRowSkeletons = [1, 2, 3];
  
  courseProgressFilters: any = {};

  setupComplete: boolean = false;
  showProgress: boolean = false;
  showMarketplaceCourses: boolean = false;
  coursesSectionTitle: string = 'My Courses';
  coursesSectionCtaText: string = 'Explore More';
  coursesSectionAriaLabel: string = 'Explore more courses';

  sectionLoading: { [prop:string]: boolean } = {};
  message: any;
  private myCoursesCount: number | null = null;
  private marketplaceEnabled: boolean = false;
  private hasUnlimitedCourseAccess: boolean = false;
  private schoolFeaturesLoaded: boolean = false;

  constructor(
    protected courseRepositoryService: CourseRepositoryService, 
    protected integrationRepositoryService: IntegrationRepositoryService,
    protected schoolRepositoryService: SchoolRepositoryService,
    private unlimitedCourseAccessService: UnlimitedCourseAccessService
  ) { }

  ngOnInit(): void {
    this.sectionLoading[DashboardSections.courseProgress] = true;
    this.sectionLoading[DashboardSections.myCourses] = true;
    this.sectionLoading[DashboardSections.myAssignments] = true;

    this.setupDashboard();
    this.loadSchoolFeatures();

    this.integrationRepositoryService.homePageIntegrationMessage.subscribe((message) => { 
      this.message = message;
    });
  }

  dataLoadComplete(section: DashboardSections) {
    setTimeout(() => {
      this.sectionLoading[section] = false;
    }, 400);
  }

  onMyCoursesLoadComplete(rows: any[] | null | undefined): void {
    this.myCoursesCount = Array.isArray(rows) ? rows.length : 0;
    this.tryShowCourseSection();
  }

  onMarketplaceLoadComplete(): void {
    this.dataLoadComplete(DashboardSections.myCourses);
  }

  setupDashboard() {

    this.courseRepositoryService.getCurrentStudentCourses()
      .subscribe((studentCourses) => {
        this.studentCourses = studentCourses;

        this.setupCurrentCourse();

      });
  }

  setupCurrentCourse() {
    this.currentStudentCourse = this.studentCourses?.find((studentCourse) => !studentCourse.CourseEndorsementStudent?.CourseEndorsementId);

    if (this.currentStudentCourse?.NextCourseLessonId) {
      this.currentStudentCourse.CourseIdDisplay;
        this.courseProgressFilters = {
          CourseLessonId: this.currentStudentCourse.NextCourseLessonId,
          StudentCourseCourseId: this.currentStudentCourse.CourseId
        }

        this.showProgress = true;
    }

    this.setupComplete = true;

    return;
  }

  private loadSchoolFeatures(): void {
    this.schoolRepositoryService.getSchoolFeatures()
      .pipe(mergeMap(schoolFeatures => this.unlimitedCourseAccessService.hasAccess(schoolFeatures)
        .pipe(map(hasAccess => ({ schoolFeatures, hasAccess })))))
      .subscribe({
        next: ({ schoolFeatures, hasAccess }) => {
          this.marketplaceEnabled = !!schoolFeatures?.IsMarketplaceEnabled;
          this.hasUnlimitedCourseAccess = hasAccess;
          this.schoolFeaturesLoaded = true;
          this.tryShowCourseSection();
        },
        error: () => {
          this.schoolFeaturesLoaded = true;
          this.tryShowCourseSection();
        }
      });
  }

  private tryShowCourseSection(): void {
    if (this.myCoursesCount === null || !this.schoolFeaturesLoaded) {
      return;
    }

    this.showMarketplaceCourses = this.myCoursesCount === 0
      && (this.marketplaceEnabled || this.hasUnlimitedCourseAccess);
    const showAvailableCourses = this.showMarketplaceCourses
      && !this.marketplaceEnabled
      && this.hasUnlimitedCourseAccess;
    this.coursesSectionTitle = showAvailableCourses
      ? 'Available Courses'
      : this.showMarketplaceCourses ? 'Marketplace' : 'My Courses';
    this.coursesSectionCtaText = showAvailableCourses
      ? 'View Available Courses'
      : this.showMarketplaceCourses ? 'Explore Marketplace' : 'Explore More';
    this.coursesSectionAriaLabel = showAvailableCourses
      ? 'View available courses'
      : this.showMarketplaceCourses ? 'Explore marketplace' : 'Explore more courses';

    if (this.showMarketplaceCourses) {
      return;
    }

    this.dataLoadComplete(DashboardSections.myCourses);
  }
}


export enum DashboardSections {
  courseProgress = 'courseProgress',
  myCourses = 'myCourses',
  myAssignments = 'myAssignments'
}
